import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { Table, TableBody } from '../../../components/ui/Table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/Dialog';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSelectedAction,
  getComparator,
  useTable
} from '../../../components/table';
import TableLoading from '../../../components/table/TableLoading';
import { useDynamicTableHeaders } from '../../../components/table/useDynamicTableHeaders';
import { CalendarReservationContentMode } from '../../../models/owner-portal/calendarEvents';
import {
  ReservationGeneral,
  ReservationGuestType
} from '../../../models/owner-portal/reservationGeneral';
import { useGetRTKQReservationsQuery } from '../../../redux/rtkQuery/apiSlice';
import { useTableWidth } from '../useTableWidth';
import ReservationTableRow from './ReservationTableRow';
import ReservationTableToolbar from './ReservationTableToolbar';
import {
  createReservationTableHead,
  defaultStageFilter,
  desktopBreakpointKeys,
  mobileBreakpointKeys,
  tabletBreakpointKeys
} from './config';
import PresetTimeRangePicker from '../../../components/date-range-picker/PresetTimeRangePicker';
import { DateBounds, PresetTimeRange } from '../../../utils/owner-portal/presetTimeRange';

interface ReservationTableProps {
  listingId?: string;
  mode?: CalendarReservationContentMode;
}
// ----------------------------------------------------------------------

export function ReservationTable(props: ReservationTableProps) {
  const { listingId, mode = CalendarReservationContentMode.All } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    selected,
    onSelectRow,
    onSelectAllRows: _onSelectAllRows,
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage
  } = useTable({
    defaultDense: true,
    defaultOrder: 'desc',
    defaultOrderBy: 'booking_date_ISO'
  });

  const [dynamicTableHeaders, tableContentColumnsToShow] =
    useDynamicTableHeaders<ReservationGeneral>({
      tableHeaders: createReservationTableHead(mode),
      breakpoints: {
        sm: mobileBreakpointKeys,
        md: tabletBreakpointKeys,
        lg: desktopBreakpointKeys
      }
    });

  const tableMinWidth = useTableWidth();

  const [resvQueryStartDate, setResvQueryStartDate] = useState<Date | null>(null);
  const [resvQueryEndDate, setResvQueryEndDate] = useState<Date | null>(null);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterUnitId, setFilterUnitId] = useState('all');
  const [filterGuestType, setFilterGuestType] = useState('all');

  //TODO: Not in use; to be repurposed for other filters (see User List).
  // const [filterRole, setFilterRole] = useState('all');
  //NOTE: Any refresh-button feature would need to reset this to null. Otherwise, the
  //      options shown in the filter may not match the data.
  const [filterOptionsUnitId, setFilterOptionsUnitId] = useState<null | string[]>(null);
  const [filterGuestTypeOptions, setFilterGuestTypeOptions] = useState<null | string[]>(null);
  const [filterStage, setFilterStage] = useState(defaultStageFilter);
  const [legalDates, setLegalDates] = useState(true);
  const [openConfirm, setOpenConfirm] = useState(false);

  const isFiltered =
    filterSearch !== '' ||
    filterUnitId !== 'all' ||
    filterGuestType !== 'all' ||
    filterUnitId !== 'all' ||
    filterStage !== defaultStageFilter;

  const {
    data: reservationData,
    // error: reservationsError,
    isFetching,
    isSuccess
    // isError
  } = useGetRTKQReservationsQuery({
    listingId: listingId,
    startDate: resvQueryStartDate,
    endDate: resvQueryEndDate
  });

  useEffect(() => {
    //? set default guest type filter from search params
    if (filterGuestTypeOptions === null) {
      return;
    }
    if (!searchParams.has('guest_type')) {
      return;
    }
    const guest_type = searchParams.get('guest_type')!;
    if (filterGuestTypeOptions.includes(guest_type)) {
      setFilterGuestType(guest_type);
    }
    setSearchParams(new URLSearchParams());
  }, [searchParams, filterGuestTypeOptions, setSearchParams]);

  const dataFiltered = applyReservationTableFilter({
    inputData: isSuccess ? reservationData : [],
    comparator: getComparator(order, orderBy),
    filterSearch,
    filterUnitId,
    filterStage,
    filterGuestType
  });
  // const denseHeight = dense ? 52 : 72;

  const anyFilterInUse = filterSearch !== '' || filterUnitId !== 'all' || filterGuestType !== 'all';

  const dataToShowExists = dataFiltered.length > 0;

  const handleOpenConfirm = () => {
    //REMINDER: This only pertains to _our_ confirm, not one opened by one of our table rows!
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    //REMINDER: This only pertains to _our_ confirm, not one opened by one of our table rows!
    setOpenConfirm(false);
  };

  // const handleFilterStage = (event: React.SyntheticEvent<Element, Event>, newValue: string) => {
  //     setPage(0);
  //     setFilterStage(newValue);
  // };

  const handleFilterSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setFilterSearch(event.target.value);
  };

  const handleFilterUnitId = (value: string) => {
    setPage(0);
    setFilterUnitId(value);
  };

  const handleFilterGuestType = (value: string) => {
    setPage(0);
    setFilterGuestType(value);
  };

  const handleResetFilter = () => {
    setFilterSearch('');
    setFilterUnitId('all');
    setFilterGuestType('all');
    setFilterStage(defaultStageFilter);
  };

  if (isSuccess) {
    if (filterOptionsUnitId === null) {
      //! build the dynamic Unit Id filter options, once we have data
      const allUnitIds = reservationData.map((resv) => resv.unit_id);
      const uniqueIds = new Set(allUnitIds);
      if (uniqueIds.has('all')) {
        uniqueIds.delete('all');
      }
      const uniqueIdArray: string[] = Array.from(uniqueIds);
      uniqueIdArray.unshift('all');
      setFilterOptionsUnitId(uniqueIdArray);
    }
    if (filterGuestTypeOptions === null) {
      //! build the guest type filter options, once we have data
      const allGuestTypes = reservationData.map((resv) => resv.display_guest_type);
      const uniqueGuestTypes = new Set(allGuestTypes);
      const uniqueGuestTypeArray = Array.from(uniqueGuestTypes) as (ReservationGuestType | 'all')[];
      uniqueGuestTypeArray.unshift('all');
      setFilterGuestTypeOptions(uniqueGuestTypeArray);
    }
  }
  return (
    <>
      {!legalDates ? (
        <div className="flex flex-col items-center m-20">
          <h3 className="text-2xl font-bold mb-4">Invalid date range.</h3>
          <p className="text-muted-foreground">Please check the start and end dates.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-4 px-4 py-4 w-full min-w-0">
            <PresetTimeRangePicker
              centered={false}
              defaultRange={PresetTimeRange.Future}
              filterEndDate={resvQueryEndDate}
              filterStartDate={resvQueryStartDate}
              usageLocation="reservations"
              onTimeRangeChanged={(dateBounds: DateBounds | null) => {
                if (dateBounds === null) {
                  setResvQueryStartDate(null);
                  setResvQueryEndDate(null);
                  return;
                }

                setResvQueryStartDate(dateBounds.start);
                setResvQueryEndDate(dateBounds.end);
              }}
              callbackSetDatesLegal={(legal: boolean) => {
                setLegalDates(legal);
              }}
            />
            <ReservationTableToolbar
              className="pt-0"
              singleListing={mode === CalendarReservationContentMode.SingleListing}
              filterSearch={filterSearch}
              filterUnitId={filterUnitId}
              isFiltered={anyFilterInUse}
              optionsUnitId={filterOptionsUnitId || ['all']}
              onFilterSearch={handleFilterSearch}
              onFilterUnitId={handleFilterUnitId}
              onResetFilter={handleResetFilter}
              onFilterGuestType={handleFilterGuestType}
              filterGuestType={filterGuestType}
              optionsGuestType={filterGuestTypeOptions || ['all']}
            />
          </div>

          <div className="relative overflow-auto">
            <TableSelectedAction
              dense={dense}
              numSelected={selected.length}
              rowCount={dataFiltered.length}
              action={
                <Button variant="ghost" size="icon" onClick={handleOpenConfirm} title="Delete">
                  <Trash2 className="h-4 w-4" />
                </Button>
              }
            />

            <div className="overflow-x-auto">
              <Table style={{ minWidth: tableMinWidth }}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={dynamicTableHeaders}
                  rowCount={dataFiltered.length}
                  numSelected={selected.length}
                  onSort={onSort}
                />

                <TableBody>
                  {dataFiltered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, i) => (
                      <ReservationTableRow
                        mode={mode}
                        key={row.id + '-' + i}
                        row={row}
                        selected={selected.includes(row.id)}
                        onSelectRow={() => onSelectRow(row.id)}
                        onDeleteRow={() => {
                          console.warn('Delete single reservation disabled.');
                        }}
                        showColumns={tableContentColumnsToShow}
                      />
                    ))}

                  {isFetching ? (
                    <TableLoading isNotFound={!dataToShowExists} />
                  ) : (
                    <TableNoData
                      isNotFound={!dataToShowExists}
                      isFiltered={isFiltered}
                      noDataText="No reservations found"
                    />
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <TablePaginationCustom
            count={dataFiltered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            showDenseToggle={false}
            dense={dense}
            onChangeDense={onChangeDense}
          />

          <Dialog open={openConfirm} onOpenChange={(open) => !open && handleCloseConfirm()}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete <strong>{selected.length}</strong> booking
                  {selected.length === 1 ? '' : 's'}?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={handleCloseConfirm}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    console.warn('Delete multiple reservations disabled.');
                  }}
                >
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

export function applyReservationTableFilter({
  inputData,
  comparator,
  filterSearch,
  filterUnitId,
  filterStage: _filterStage,
  filterGuestType
}: {
  inputData: ReservationGeneral[];
  comparator: (a: any, b: any) => number;
  filterSearch: string;
  filterUnitId: string;
  filterStage: string;
  filterGuestType: string;
}) {
  const stabilizedThis = inputData.map((el, index) => [el, index] as const);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  inputData = stabilizedThis.map((el) => el[0]);

  if (filterSearch) {
    const filterSearchLower = filterSearch.toLowerCase();
    inputData = inputData.filter((reservation) => {
      return (
        (reservation.unit_id?.toLowerCase()?.indexOf(filterSearchLower) ?? -1) !== -1 ||
        (reservation.guest_name?.toLowerCase()?.indexOf(filterSearchLower) ?? -1) !== -1 ||
        (reservation.name?.toLowerCase()?.indexOf(filterSearchLower) ?? -1) !== -1 ||
        (reservation.ui_table_guest_name?.toLowerCase()?.indexOf(filterSearchLower) ?? -1) !== -1
      );
    });
  }

  // this filter is not in use, but it has come and gone; could be back
  // if (filterStage !== 'all') {
  //     inputData = inputData.filter((reservation) => reservation.stage_mapped === filterStage);
  // }

  if (filterUnitId !== 'all') {
    const filterUnitIdLower = filterUnitId.toLowerCase();
    inputData = inputData.filter((reservation) => {
      if (!reservation.unit_id) {
        return false;
      }
      return reservation.unit_id.toLowerCase() === filterUnitIdLower;
    });
  }

  if (filterGuestType !== 'all') {
    const filterGuestTypeLower = filterGuestType.toLowerCase();
    inputData = inputData.filter((reservation) => {
      if (!reservation.display_guest_type) {
        return false;
      }
      return reservation.display_guest_type.toLowerCase() === filterGuestTypeLower;
    });
  }

  return inputData;
}
